import { Cog8ToothIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Assistant } from '@langchain/langgraph-sdk';
import { Button } from '@mui/joy';
import {
  Content as DialogContent,
  DialogOverlay,
  Portal as DialogPortal,
  Root as DialogRoot,
} from '@radix-ui/react-dialog';

import React from 'react';

import {
  TabGroup,
  TabLabel,
  TabList,
  TabPanel,
  TabPanels,
} from '@/components/Tabs/Tabs';
import { useSetTab } from '@/components/Tabs/useTab';

import { GraphDatasetNodeTable } from './GraphDatasetNodeTable';
import {
  GraphDatasetTableSettings,
  GraphNodeTableItem,
} from './GraphDatasetProvider.types';

interface SettingsDialogProps {
  onClose: () => void;
  /**
   * Data for displaying the graphs and graph nodes in the table.
   */
  value: GraphDatasetTableSettings;
  /**
   * The selected assistant of which the user is viewing the graph through.
   */
  selectedAssistant: Assistant | undefined;
  /**
   * Update the temporary state of the dataset settings in the dialog.
   * These changes are not saved until the user confirms the settings.
   */
  onItemChange: (graphNodeItem: GraphNodeTableItem) => void;
  /**
   * Confirm the settings set in the dialog. This updates the parent state, which in turn updates local storage.
   * If the dialog is exited without confirming, the changes made in the dialog are discarded.
   */
  onConfirm: () => void;
}

export function GraphDatasetSettingsDialog(props: SettingsDialogProps) {
  const [changesMade, setChangesMade] = React.useState(false);
  const setTab = useSetTab({ method: 'merge', prefix: 'graphs' });

  const handleClose = () => {
    if (changesMade) {
      const confirmClose = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmClose) return;
    }
    props.onClose();
  };

  return (
    <DialogRoot
      open
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-[60] bg-black/50" />
        <DialogContent className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="flex max-h-[80vh] w-full max-w-[819px] flex-col overflow-y-auto rounded-xl bg-popover p-6">
            <div className="mb-6 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="rounded-lg border-[1px] border-primary p-1 text-tertiary">
                  <Cog8ToothIcon className="h-6 w-6" />
                </div>
                <button
                  type="button"
                  onClick={() => props.onClose()}
                  className="rounded-full p-1 text-tertiary transition-colors hover:bg-secondary-hover active:bg-secondary"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div>
                <h2 className="text-2xl font-semibold">
                  Add to Dataset Settings
                </h2>
                <p className="text-sm text-tertiary">
                  Configure which nodes to add to datasets and their default
                  destinations
                </p>
              </div>
            </div>

            <div className="flex-grow rounded-lg border border-secondary">
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <span className="text-base font-medium">
                    {props.selectedAssistant?.name ??
                      props.selectedAssistant?.graph_id}
                  </span>
                  <div className="flex items-center justify-center rounded-full border border-brand-subtle bg-brand-secondary pl-2 pr-2 text-xs font-medium">
                    {props.value.graphs?.[0]?.nodes?.length} Nodes
                  </div>
                </div>
              </div>

              <TabGroup prefix="graphs" setTabMethod="merge" defaultIndex={0}>
                <TabList className="mx-0 mb-0">
                  {props.value.graphs.map((graph, idx) => {
                    let tabLabelText = 'Nodes';
                    if (graph.isSubgraph) {
                      tabLabelText = `Subgraph ${graph.name}`;
                    }
                    return (
                      <span
                        onClick={() => setTab(idx)}
                        key={`tab-list-label-${idx}`}
                      >
                        <TabLabel className="text-sm text-tertiary">
                          {tabLabelText}
                        </TabLabel>
                      </span>
                    );
                  })}
                </TabList>
                <TabPanels>
                  {props.value.graphs.map((graph, index) => (
                    <TabPanel key={`tab-panel-${index}`}>
                      <GraphDatasetNodeTable
                        setGraphTabIndex={setTab}
                        graphNodeTableItems={graph.nodes}
                        handleDatasetChange={(delta) => {
                          props.onItemChange(delta);
                          setChangesMade(true);
                        }}
                      />
                    </TabPanel>
                  ))}
                </TabPanels>
              </TabGroup>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button color="neutral" variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button color="primary" onClick={props.onConfirm}>
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
}
